import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    AspectRatio,
    Box,
    ColorSwatch,
    Flex,
    Grid,
    Image,
    Paper,
    Skeleton,
    Text,
    TextInput,
} from "@mantine/core";
import { Tenant } from "src/graphql/generated/graphql";

import { FormItem } from "../../../../../FormItem";

export interface StampWidgetProps {
    tenant: Tenant;
}

export const StampWidget = ({ tenant }: StampWidgetProps) => {
    const { t } = useTranslation(["common"]);

    const hasStamp = !!tenant?.stamp?.file?.id && !!tenant?.stamp?.file?.objectKey && !!tenant?.stamp?.file?.src;

    const stampRef = useRef<HTMLImageElement>();
    const [isStampLoaded, setIsStampLoaded] = useState(false);
    const [ratingPreview, setRatingPreview] = useState("37%");

    const {
        height: ratingPreviewHeight,
        top: ratingPreviewTop,
    } = useMemo(() => {
        if (!hasStamp || !isStampLoaded) {
            return {
                height: 0,
                top: 0,
            };
        }

        const height = Math.round((tenant.stamp.ratingHeight / tenant.stamp.height) * stampRef.current?.clientHeight * 100) / 100;
        const top = Math.round((tenant.stamp.ratingTop / tenant.stamp.height) * stampRef.current?.clientHeight * 100) / 100;

        return { height, top };
    }, [hasStamp, isStampLoaded, tenant?.stamp?.height, tenant?.stamp?.ratingHeight, tenant?.stamp?.ratingTop]);

    return (
        <Paper
            pos="relative"
            p={36}
            radius={12}
        >
            <Flex align="center" justify="space-between">
                <Text weight={500}>
                    {t("tenant_stamp")}
                </Text>
                <Box />
            </Flex>
            <Box mt="md">
                {!hasStamp && (
                    t("tenant_stamp_missing")
                )}
                {hasStamp && (
                    <Flex wrap="wrap-reverse">
                        <Box
                            pos="relative"
                            display="inline-block"
                            w="200px"
                            style={{
                                transform: tenant.stamp.rotationDeg ?
                                    `rotate(${tenant.stamp.rotationDeg}deg)` : undefined,
                            }}
                        >
                            <AspectRatio
                                ratio={1}
                                maw="200px"
                            >
                                <>
                                    {!isStampLoaded && (
                                        <Skeleton radius="xl" />
                                    )}
                                    <Image
                                        src={tenant?.stamp?.file?.src}
                                        ref={stampRef}
                                        display={isStampLoaded ?
                                            undefined : "none"}
                                        onLoad={() =>
                                            setIsStampLoaded(true)}
                                    />
                                </>
                            </AspectRatio>
                            {isStampLoaded && (
                                <Box
                                    pos="absolute"
                                    display="inline-block"
                                    style={{
                                        transform: "translate(-50%,0)",
                                        left: "50%",
                                        top: ratingPreviewTop,
                                        height: ratingPreviewHeight,
                                        fontSize: `${ratingPreviewHeight}px`,
                                        fontFamily: tenant.stamp.ratingFont,
                                        color: tenant.stamp.ratingColor,
                                    }}
                                >
                                    {ratingPreview}
                                </Box>
                            )}
                        </Box>
                        <Grid
                            ml="xl"
                            miw="100px"
                            style={{ flex: 1 }}
                        >
                            <Grid.Col xs={12} md={6}>
                                <TextInput
                                    label={t("tenant_stamp_ratingpreview")}
                                    placeholder={t("tenant_stamp_ratingpreview")}
                                    value={ratingPreview}
                                    onChange={event =>
                                        setRatingPreview(event?.target?.value)}
                                />
                            </Grid.Col>
                            <Grid.Col xs={12} md={6}>
                                <FormItem label={t("tenant_stamp_rotationdeg")}>
                                    {tenant.stamp.rotationDeg ? `${tenant.stamp.rotationDeg} °` : <Text color="dimmed">-</Text>}
                                </FormItem>
                            </Grid.Col>
                            <Grid.Col xs={12} md={6}>
                                <FormItem label={t("tenant_stamp_ratingfont")}>
                                    {tenant.stamp.ratingFont}
                                </FormItem>
                            </Grid.Col>
                            <Grid.Col xs={12} md={6}>
                                <FormItem label={t("tenant_stamp_ratingcolor")}>
                                    <ColorSwatch color={tenant.stamp.ratingColor} />
                                </FormItem>
                            </Grid.Col>
                            <Grid.Col xs={12} md={6}>
                                <FormItem label={t("tenant_stamp_ratingtop")}>
                                    {tenant.stamp.ratingTop}px
                                </FormItem>
                            </Grid.Col>
                            <Grid.Col xs={12} md={6}>
                                <FormItem label={t("tenant_stamp_ratingheight")}>
                                    {tenant.stamp.ratingHeight}px
                                </FormItem>
                            </Grid.Col>
                        </Grid>
                    </Flex>
                )}
            </Box>
        </Paper>
    );
};
